// src/UserPrefsProvider.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthProvider';
import { applyTheme } from './utils/applyTheme';
import dexie from './offlineDB';
import { addPendingWrite } from './utils/flushPendingWrites'; // ✅ this is your real file

const UserPrefsContext = createContext({
  prefs: null,
  setPrefs: () => {},
  savePrefs: async () => {}
});

export function useUserPrefs() {
  return useContext(UserPrefsContext);
}

export function UserPrefsProvider({ children }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);

 useEffect(() => {
  if (!user) return;

  const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');
  console.log('[UserPrefsProvider] Listening to:', prefsRef.path);

  const fallback = {
    defaultGenre: '',
    defaultType: '',
    defaultKey: '',
    defaultStatus: '',
    theme: 'light',
    customBgColor: '#ffffff'
  };

  if (navigator.onLine) {
    const unsubscribe = onSnapshot(
      prefsRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          console.log('[UserPrefsProvider] Firestore data loaded:', data);
          setPrefs(data);
          applyTheme(data.theme, data.customBgColor);
          dexie.preferences.put({ id: 'settings', ...data });
        } else {
          console.warn('[UserPrefsProvider] No prefs doc found. Setting fallback.');
          // attempt to create prefs if not present
          setDoc(prefsRef, fallback).catch(console.error);
          setPrefs(fallback);
          applyTheme(fallback.theme, fallback.customBgColor);
        }
      },
      (error) => {
        console.error('[UserPrefsProvider] onSnapshot failed:', error);
        // fail-safe: use local fallback even if error
        setPrefs(fallback);
        applyTheme(fallback.theme, fallback.customBgColor);
      }
    );
    return () => unsubscribe();
  } else {
    // 🔁 Load from Dexie if offline
    dexie.preferences.get('settings').then((localPrefs) => {
      if (localPrefs) {
        console.log('[UserPrefsProvider] Loaded prefs from Dexie:', localPrefs);
        setPrefs(localPrefs);
        applyTheme(localPrefs.theme, localPrefs.customBgColor);
      } else {
        console.warn('[UserPrefsProvider] Dexie empty. Setting fallback.');
        setPrefs(fallback);
        applyTheme(fallback.theme, fallback.customBgColor);
      }
    });
  }
}, [user]);


async function savePrefs(newPrefs) {
  const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');
  if (navigator.onLine) {
    await setDoc(prefsRef, newPrefs, { merge: true });
  } else {
    await dexie.preferences.put({ id: 'settings', ...newPrefs });
    await addPendingWrite({
      type: 'update',
      collection: 'preferences',
      docId: 'settings',
      data: newPrefs
    });
  }
  setPrefs(newPrefs); // update local state regardless
}

  return (
<UserPrefsContext.Provider value={{ prefs, setPrefs, savePrefs }}>
      {children}
    </UserPrefsContext.Provider>
  );
}
